import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazyWithRetry as lazy } from '../utils/lazyWithRetry';
import { RouteErrorBoundary } from './RouteErrorBoundary';

// Layouts
const RootLayout = lazy(() => import('../layouts/RootLayout'));
const AppLayout = lazy(() => import('../layouts/AppLayout'));
const AdminLayout = lazy(() => import('../layouts/AdminLayout'));

// Public pages
const LandingPage = lazy(() => import('../pages/LandingPage'));
const AuthPage = lazy(() => import('../pages/AuthPage'));
const ProfileSetupPage = lazy(() => import('../pages/ProfileSetupPage'));
const OrgSetupPage = lazy(() => import('../pages/OrgSetupPage'));
const OrgSelectorPage = lazy(() => import('../pages/OrgSelectorPage'));
const JoinPage = lazy(() => import('../pages/JoinPage'));

// App pages
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const SnackDetailPage = lazy(() => import('../pages/SnackDetailPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const CopilotPage = lazy(() => import('../pages/admin/CopilotPage'));
const CameronPage = lazy(() => import('../pages/admin/CameronPage'));
const UsersPage = lazy(() => import('../pages/admin/UsersPage'));
const AssignmentsPage = lazy(() => import('../pages/admin/AssignmentsPage'));
const AnalyticsPage = lazy(() => import('../pages/admin/AnalyticsPage'));
const BillingPage = lazy(() => import('../pages/admin/BillingPage'));
const OrgSettingsPage = lazy(() => import('../pages/admin/OrgSettingsPage'));
const SuperuserPage = lazy(() => import('../pages/admin/SuperuserPage'));
const CopilotMetricsPage = lazy(() => import('../pages/admin/CopilotMetricsPage'));
const CopilotTracePage = lazy(() => import('../pages/admin/CopilotTracePage'));
const TrainingReportsPage = lazy(() => import('../pages/admin/TrainingReportsPage'));

// Error and 404 pages
const NotFoundPage = lazy(() => import('../components/NotFoundPage'));

export const router = createBrowserRouter([
  {
    // RootLayout wraps ALL routes to provide AuthProvider
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Public routes
      { path: '/', element: <LandingPage /> },
      { path: '/auth', element: <AuthPage /> },
      { path: '/setup/profile', element: <ProfileSetupPage /> },
      { path: '/setup/organization', element: <OrgSetupPage /> },
      { path: '/select-org', element: <OrgSelectorPage /> },
      { path: '/join', element: <JoinPage /> },

      // App routes (wrapped by AppLayout for auth protection)
      {
        path: '/app',
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'snack/:snackId', element: <SnackDetailPage /> },
        ],
      },

      // Admin routes (wrapped by AdminLayout for admin protection)
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <AdminDashboardPage /> },
          { path: 'copilot', element: <CopilotPage /> },
          { path: 'design', element: <CameronPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'assignments', element: <AssignmentsPage /> },
          { path: 'analytics', element: <AnalyticsPage /> },
          { path: 'analytics/triage/:issueId', element: <AnalyticsPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'reports', element: <TrainingReportsPage /> },
          { path: 'org', element: <OrgSettingsPage /> },
          { path: 'superuser', element: <SuperuserPage /> },
          // Superuser-only copilot observability (Phase 3 PR-C). Both pages
          // re-assert the superuser check themselves — AdminLayout gates on
          // ADMIN, which is a weaker claim than these pages require.
          { path: 'copilot-metrics', element: <CopilotMetricsPage /> },
          { path: 'copilot-trace/:runId', element: <CopilotTracePage /> },
        ],
      },

      // Catch-all 404 route
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
